// TimeInput — streaming auto-tab time entry component
// Segments: [HH] : [MM] [AM/PM]
// UX: type 2 digits for hours → auto-advance to minutes
//     type 2 digits for minutes → auto-advance to AM/PM
//     press A or P → set AM/PM and advance to next field

import React, { useRef, useState, useEffect, useCallback } from 'react';
import './TimeInput.css';

export interface TimeInputProps {
  /** 24h format "HH:MM", or "" for no value */
  value: string;
  /** Called with 24h "HH:MM" whenever a complete time is entered */
  onChange: (value: string) => void;
  className?: string;
  id?: string;
  'aria-label'?: string;
  disabled?: boolean;
}

// ── Conversion helpers ──────────────────────────────────────────────────────

function parse24h(v: string): { h: string; m: string; period: 'AM' | 'PM' } | null {
  const match = /^(\d{2}):(\d{2})$/.exec(v);
  if (!match) return null;
  const h24 = parseInt(match[1]!, 10);
  const period: 'AM' | 'PM' = h24 < 12 ? 'AM' : 'PM';
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return { h: String(h12).padStart(2, '0'), m: match[2]!, period };
}

function build24h(h: string, m: string, period: 'AM' | 'PM'): string {
  if (!h || !m) return '';
  const hNum = parseInt(h, 10);
  const mNum = parseInt(m, 10);
  if (isNaN(hNum) || isNaN(mNum) || hNum < 1 || hNum > 12 || mNum > 59) return '';
  let h24 = hNum;
  if (period === 'AM' && hNum === 12) h24 = 0;
  else if (period === 'PM' && hNum !== 12) h24 += 12;
  return `${String(h24).padStart(2, '0')}:${String(mNum).padStart(2, '0')}`;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// ── Component ───────────────────────────────────────────────────────────────

export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  className,
  id,
  'aria-label': ariaLabel,
  disabled = false,
}) => {
  const hoursRef = useRef<HTMLInputElement>(null);
  const minutesRef = useRef<HTMLInputElement>(null);
  const periodRef = useRef<HTMLButtonElement>(null);

  // Track the last value we emitted to avoid re-sync loops
  const lastEmitted = useRef(value);
  // Entry mode: first keypress replaces instead of appending
  const hoursReplace = useRef(false);
  const minutesReplace = useRef(false);

  const init = parse24h(value);
  const [hours, setHours] = useState(init?.h ?? '');
  const [minutes, setMinutes] = useState(init?.m ?? '');
  const [period, setPeriod] = useState<'AM' | 'PM'>(init?.period ?? 'AM');

  // Sync from external value changes (skip if caused by our own emit)
  useEffect(() => {
    if (value !== lastEmitted.current) {
      const parsed = parse24h(value);
      setHours(parsed?.h ?? '');
      setMinutes(parsed?.m ?? '');
      setPeriod(parsed?.period ?? 'AM');
      lastEmitted.current = value;
    }
  }, [value]);

  const emit = useCallback(
    (h: string, m: string, p: 'AM' | 'PM') => {
      const v = build24h(h, m, p);
      if (v) {
        lastEmitted.current = v;
        onChange(v);
      }
    },
    [onChange]
  );

  // Advance focus to the next focusable element after the period button
  const advancePastPeriod = useCallback(() => {
    const el = periodRef.current;
    if (!el) return;
    const all = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE));
    const idx = all.indexOf(el);
    if (idx >= 0 && idx < all.length - 1) {
      all[idx + 1]?.focus();
    }
  }, []);

  // ── Hours segment ──────────────────────────────────────────────────────────

  const handleHoursFocus = () => {
    hoursReplace.current = true;
    hoursRef.current?.select();
  };

  const handleHoursKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      setHours('');
      hoursReplace.current = false;
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      minutesRef.current?.focus();
      return;
    }
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    const d = parseInt(e.key, 10);
    const isFirstEntry = hoursReplace.current || hours === '';

    if (isFirstEntry) {
      hoursReplace.current = false;
      if (d >= 2) {
        // Single-digit hour 2-9 is complete — advance immediately
        const h = String(d).padStart(2, '0');
        setHours(h);
        emit(h, minutes, period);
        minutesRef.current?.focus();
        minutesReplace.current = true;
      } else {
        setHours(String(d)); // 0 or 1 — wait for second digit
      }
    } else if (hours.length === 1) {
      const candidate = hours + String(d);
      const num = parseInt(candidate, 10);
      if (num >= 1 && num <= 12) {
        const h = num < 10 ? '0' + num : String(num);
        setHours(h);
        emit(h, minutes, period);
        minutesRef.current?.focus();
        minutesReplace.current = true;
      } else {
        // num === 0 (e.g. "00") or > 12 — treat new digit as fresh start
        if (d >= 2) {
          const h = String(d).padStart(2, '0');
          setHours(h);
          emit(h, minutes, period);
          minutesRef.current?.focus();
          minutesReplace.current = true;
        } else {
          setHours(String(d)); // new first digit 0 or 1
        }
      }
    } else {
      // Hours already complete — restart from this digit
      hoursReplace.current = false;
      if (d >= 2) {
        const h = String(d).padStart(2, '0');
        setHours(h);
        emit(h, minutes, period);
        minutesRef.current?.focus();
        minutesReplace.current = true;
      } else {
        setHours(String(d));
      }
    }
  };

  // ── Minutes segment ────────────────────────────────────────────────────────

  const handleMinutesFocus = () => {
    minutesReplace.current = true;
    minutesRef.current?.select();
  };

  const handleMinutesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      setMinutes('');
      minutesReplace.current = false;
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      hoursRef.current?.focus();
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      periodRef.current?.focus();
      return;
    }
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    const d = parseInt(e.key, 10);
    const isFirstEntry = minutesReplace.current || minutes === '';

    if (isFirstEntry) {
      minutesReplace.current = false;
      if (d >= 6) {
        // No valid 2-digit minute starts with 6-9 — auto-advance
        const m = String(d).padStart(2, '0');
        setMinutes(m);
        emit(hours, m, period);
        periodRef.current?.focus();
      } else {
        setMinutes(String(d));
      }
    } else if (minutes.length === 1) {
      const candidate = minutes + String(d);
      const num = parseInt(candidate, 10);
      if (num <= 59) {
        setMinutes(candidate);
        emit(hours, candidate, period);
        periodRef.current?.focus();
      } else {
        // Invalid — restart with this digit
        if (d >= 6) {
          const m = String(d).padStart(2, '0');
          setMinutes(m);
          emit(hours, m, period);
          periodRef.current?.focus();
        } else {
          setMinutes(String(d));
        }
      }
    } else {
      // Minutes already complete — restart
      minutesReplace.current = false;
      if (d >= 6) {
        const m = String(d).padStart(2, '0');
        setMinutes(m);
        emit(hours, m, period);
        periodRef.current?.focus();
      } else {
        setMinutes(String(d));
      }
    }
  };

  // ── AM/PM segment ──────────────────────────────────────────────────────────

  const handlePeriodKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Tab') return;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      minutesRef.current?.focus();
      return;
    }

    const key = e.key.toUpperCase();
    if (key === 'A') {
      e.preventDefault();
      setPeriod('AM');
      emit(hours, minutes, 'AM');
      advancePastPeriod();
    } else if (key === 'P') {
      e.preventDefault();
      setPeriod('PM');
      emit(hours, minutes, 'PM');
      advancePastPeriod();
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      const p: 'AM' | 'PM' = period === 'AM' ? 'PM' : 'AM';
      setPeriod(p);
      emit(hours, minutes, p);
      advancePastPeriod();
    }
  };

  const handlePeriodClick = () => {
    const p: 'AM' | 'PM' = period === 'AM' ? 'PM' : 'AM';
    setPeriod(p);
    emit(hours, minutes, p);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      className={`time-input-widget${className ? ` ${className}` : ''}`}
      id={id}
      role="group"
      aria-label={ariaLabel ?? 'Time input'}
    >
      <input
        ref={hoursRef}
        type="text"
        inputMode="numeric"
        className="time-input-seg time-input-hours"
        value={hours}
        placeholder="--"
        onKeyDown={handleHoursKeyDown}
        onFocus={handleHoursFocus}
        onChange={() => {/* controlled — keyboard only */}}
        aria-label="Hours"
        disabled={disabled}
        maxLength={2}
        tabIndex={0}
      />
      <span className="time-input-sep" aria-hidden="true">:</span>
      <input
        ref={minutesRef}
        type="text"
        inputMode="numeric"
        className="time-input-seg time-input-minutes"
        value={minutes}
        placeholder="--"
        onKeyDown={handleMinutesKeyDown}
        onFocus={handleMinutesFocus}
        onChange={() => {/* controlled — keyboard only */}}
        aria-label="Minutes"
        disabled={disabled}
        maxLength={2}
        tabIndex={0}
      />
      <button
        ref={periodRef}
        type="button"
        className="time-input-period"
        onKeyDown={handlePeriodKeyDown}
        onClick={handlePeriodClick}
        aria-label={`Toggle AM/PM, currently ${period}`}
        disabled={disabled}
        tabIndex={0}
      >
        {period}
      </button>
    </div>
  );
};

export default TimeInput;
